/**
 * Contact 상세 — jurisdiction(MY/SG)별 싱크 필드 변경 인디케이터.
 */
import { ref, watch, computed, type Ref } from 'vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { fetchSyncHistoryCached as fetchMySyncHistoryCached } from '@features/Entity_my/utils/syncHistoryCache';
import {
    buildFieldChangeMap as buildMyFieldChangeMap,
    type MySyncHistoryGroup,
    type SyncFieldChange as MySyncFieldChange,
} from '@features/Entity_my/utils/syncHistoryAdapter';
import { fetchSyncHistoryCached as fetchSgSyncHistoryCached } from '@features/Entity/utils/syncHistoryCache';
import {
    buildFieldChangeMap as buildSgFieldChangeMap,
    type SyncFieldChange as SgSyncFieldChange,
} from '@features/Entity/utils/syncHistoryAdapter';

export type SyncFieldChange = MySyncFieldChange | SgSyncFieldChange;

export interface SyncFieldMatch {
    identificationNumber?: string;
    contactCd?: string;
    profileId?: string;
    personName?: string;
}

export function useSyncFieldChanges(entityCd: Ref<string>, match: Ref<SyncFieldMatch>) {
    const sessionStore = useSessionStore();
    const syncFieldChanges = ref<Map<string, SyncFieldChange>>(new Map());

    const isMyJurisdiction = computed(() => {
        const j = String(sessionStore.user?.customer?.jurisdiction || '').toUpperCase();
        return j === 'MY' || j === 'MALAYSIA';
    });

    async function load() {
        const ecd = String(entityCd.value || '').trim();
        const m = match.value || {};
        if (!ecd || (!m.identificationNumber && !m.contactCd && !m.profileId && !m.personName)) {
            syncFieldChanges.value = new Map();
            return;
        }
        try {
            if (isMyJurisdiction.value) {
                const res: any = await fetchMySyncHistoryCached(ecd);
                const groups: MySyncHistoryGroup[] = Array.isArray(res?.history) ? res.history : [];
                syncFieldChanges.value = buildMyFieldChangeMap(groups, m);
            } else {
                const res = await fetchSgSyncHistoryCached(ecd);
                syncFieldChanges.value = buildSgFieldChangeMap(res.history, m);
            }
        } catch {
            syncFieldChanges.value = new Map();
        }
    }

    watch([entityCd, match, isMyJurisdiction], load, { immediate: true, deep: true });

    function fieldChange(...fieldNames: string[]): SyncFieldChange | null {
        for (const f of fieldNames) {
            const hit = syncFieldChanges.value.get(f);
            if (hit) return hit;
        }
        return null;
    }

    return { syncFieldChanges, fieldChange, reloadSyncFieldChanges: load };
}
